import type { ApiBaseResponse, CtsUrlInterface } from '@/types/types';
import apiAxios from '@/middleware/axiosMiddleware';
import type { AxiosRequestConfig, AxiosResponse } from 'axios';

export type RequestOptions = {
  method?: string;
  headers?: Record<string, string>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  body?: any;
  cookie?: string;
  params?: Record<string, unknown>;
  cache?: RequestCache;
};

function filterKeys(params: RequestOptions['params']) {
  if (!params) return {};
  return Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null,
    ),
  );
}

export function buildUrlWithParams(
  url: string,
  params?: RequestOptions['params'],
): string {
  if (!params) return url;
  const filteredParams = filterKeys(params);
  if (Object.keys(filteredParams).length === 0) return url;
  const queryString = new URLSearchParams(
    filteredParams as Record<string, string>,
  ).toString();
  return `${url}?${queryString}`;
}

class ApiService {
  private config: CtsUrlInterface;

  constructor(config: CtsUrlInterface) {
    this.config = config;
  }

  async get<T = ApiBaseResponse>(
    endpoint: string,
    params?: RequestOptions['params'],
    config?: AxiosRequestConfig<unknown>,
  ): Promise<AxiosResponse<ApiBaseResponse<T>>> {
    const url: string = this.config.baseUrl.concat(endpoint);
    const fullUrl = buildUrlWithParams(url, params);
    return apiAxios.get(fullUrl, config);
  }

  async getFile<T = ApiBaseResponse>(
    endpoint: string,
    params?: RequestOptions['params'],
    config?: AxiosRequestConfig<unknown>,
  ): Promise<AxiosResponse<T>> {
    const url: string = this.config.baseUrl.concat(endpoint);
    const fullUrl = buildUrlWithParams(url, params);
    return apiAxios.get(fullUrl, config);
  }

  async post<T>(
    endpoint: string,
    data: unknown,
  ): Promise<AxiosResponse<ApiBaseResponse<T>>> {
    const url = `${this.config.baseUrl}${endpoint}`;
    return apiAxios.post(url, data);
  }

  async postUpload<T>(
    endpoint: string,
    data: unknown,
    uploadProgress: (percent: number) => void,
  ): Promise<AxiosResponse<ApiBaseResponse<T>>> {
    const url = `${this.config.baseUrl}${endpoint}`;
    return apiAxios.post(url, data, {
      onUploadProgress(progressEvent) {
        const { loaded, total } = progressEvent;
        const percentCompleted = Math.round((loaded * 100) / (total || 1));
        uploadProgress(percentCompleted);
      },
    });
  }

  async getFileByPost<T = ApiBaseResponse>(
    endpoint: string,
    params?: RequestOptions['params'],
    config?: AxiosRequestConfig<unknown>,
    body?: unknown,
  ): Promise<AxiosResponse<T>> {
    const url: string = this.config.baseUrl.concat(endpoint);
    const fullUrl = buildUrlWithParams(url, params);
    return apiAxios.post(fullUrl, body, config);
  }

  async put<T>(
    endpoint: string,
    data?: unknown,
  ): Promise<AxiosResponse<ApiBaseResponse<T>>> {
    const url = `${this.config.baseUrl}${endpoint}`;
    return apiAxios.put(url, data);
  }

  async patch<T>(
    endpoint: string,
    data?: unknown,
  ): Promise<AxiosResponse<ApiBaseResponse<T>>> {
    const url = `${this.config.baseUrl}${endpoint}`;
    return apiAxios.patch(url, data);
  }

  async delete<T>(
    endpoint: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    params?: any,
  ): Promise<AxiosResponse<ApiBaseResponse<T>>> {
    const url = `${this.config.baseUrl}${endpoint}`;
    return apiAxios.delete(url, params);
  }
}

export default ApiService;
